import { Auth0Client } from '@auth0/auth0-spa-js';
import auth0 from 'auth0-js';
import { getMe } from '@/api/mypage';

// tbh this could also be made into a mixin, ala behavior log and user storage

import {
  registerUser,
  completeRegistration,
  changePassword,
  withdrawUser,
} from '@/api/registration';

const auth0SpaClient = new Auth0Client({
  domain: process.env.VUE_APP_AUTH0_DOMAIN,
  client_id: process.env.VUE_APP_AUTH0_CLIENT_ID,
  scope: 'profile openid email read:current_user update:current_user_metadata',
  redirect_uri: `${window.location.protocol}//${window.location.host}/member/login`,
  cacheLocation: 'localstorage',
  audience: `https://${process.env.VUE_APP_AUTH0_DOMAIN}/api/v2/`,
  useRefreshTokens: true,
});


function parseErrorCode(errorMessage) {
  const regex = /\[(.*?)\]/g;
  const match = errorMessage.match(regex);
  if (!match) {
    return null;
  }
  let replaced = match[0].replace('[', '');
  replaced = replaced.replace(']', '');
  return replaced;
}

function saveRegisterRedirect() {
  localStorage.setItem('auth0_regist_redirect', window.location.pathname + window.location.search);
}

function cacheMyUserId(id) {
  localStorage.setItem('keiai___my_ki_user_id', id);
}

function getMyUserId() {
  return localStorage.getItem('keiai___my_ki_user_id');
}

function clearMyUserId() {
  localStorage.setItem('keiai___my_ki_user_id', '');
}

const completeSignUpFlow = async (email, code, password, localAnonymousId, attrs) => {
  const params = {
    email,
    code,
    password,
    anonymousUserId: localAnonymousId,
    user_metadata: {
      ...attrs,
    },
  };
  try {
    await completeRegistration(params);
  } catch (e) {
    const errorCode = parseErrorCode(e.message) || e.code;
    if (errorCode === 'code-expired') {
      throw new Error('招待の有効期限が切れました。また最初からやり直して下さい。');
    } else {
      throw new Error('エラーが発生しました。また最初からやり直して下さい。');
    }
  }
};

// TESTING ONLY
async function getApiTokenSilently() {
  let token;
  try {
    token = await auth0SpaClient.getTokenSilently({
      scope: 'read:usertoken',
      audience: 'https://custom-api/',
    });
  } catch (e) {
    token = await auth0SpaClient.getTokenWithPopup({
      scope: 'read:usertoken',
      audience: 'https://custom-api/',
    });
  }
  return token;
}

// TESTING ONLY
async function withdraw(userId) {
  // const token = await auth0SpaClient.getTokenSilently();
  return withdrawUser(userId);
}

async function renewSession() {
  return auth0SpaClient.getTokenSilently({
    scope: 'profile openid email read:current_user',
    ignoreCache: true,
  });
}

async function updateUserMetadata(userId, newAttributes) {
  const token = await auth0SpaClient.getTokenSilently({
    scope: 'profile openid email read:current_user update:current_user_metadata',
    ignoreCache: false,
  });
  const management = new auth0.Management({
    domain: process.env.VUE_APP_AUTH0_DOMAIN,
    token,
  });

  return new Promise(async (resolve, reject) => {
    management.patchUserAttributes(userId, newAttributes, async (err, response) => {
      if (err) {
        reject(err);
      }
      // need to get new token with updated user metadata
      await renewSession();
      resolve(response);
    });
  });
}

async function updatePassword(email, currentPassword, newPassword) {
  const body = {
    email,
    old: currentPassword,
    new: newPassword,
  };
  return changePassword(body);
}

async function signIn() {
  localStorage.setItem('auth0_login_redirect', window.location.href);
  auth0SpaClient.loginWithRedirect({
    scope: 'profile openid email read:current_user',
    prompt: 'login',
  });
}

async function parseUser(decodedUser) {
  const user = {
    ...decodedUser,
    user_metadata: decodedUser['https://realestate.ki-group.jp/claims/user_metadata'],
  };
  delete user['https://realestate.ki-group.jp/claims/user_metadata'];
  return user;
}

// eslint-disable-next-line consistent-return
async function checkSession() {
  try {
    let user;
    user = await auth0SpaClient.getUser();
    if (!user) {
      // this shoudl be checkSession but auth0.is.authenticated cookie is not getting set in SSO scenario
      await auth0SpaClient.getTokenSilently();
      user = await auth0SpaClient.getUser();
      if (!user) {
        clearMyUserId();
        return false;
      }
    }
    try {
      getMe();
    } catch (e) {
      // kind of need to gfigure out what should happen here in the off chance that this api fails
      // user should be in invalid state without an id to track they're behavior information probably
    }
    return parseUser(user);
  } catch (error) {
    clearMyUserId();
    if (error.error !== 'login_required') {
      throw error;
    }
  }
}

async function signOut() {
  clearMyUserId();
  auth0SpaClient.logout({ returnTo: `${window.location.origin}?signoutSSO=true`, federated: true });
}

async function parseUserLogin() {
  await auth0SpaClient.handleRedirectCallback();
  const redirectUrl = localStorage.getItem('auth0_login_redirect');
  localStorage.removeItem('auth0_login_redirect');
  return redirectUrl;
}


async function signUp(email) {
  try {
    return await registerUser({ email, origin: window.location.origin });
  } catch (err) {
    const errorCode = parseErrorCode(err.message) || err.code;
    if (errorCode === 'user_exists') {
      throw new Error('このメールアドレスはすでに登録されています。');
    } else {
      throw new Error('エラーが発生しました。しばらく待ってからやり直してください。');
    }
  }
}

async function signOutAllServices() {
  const services = [
    {
      name: 'my-page',
      signOutUrl: `https://${process.env.VUE_APP_MYPAGE_DOMAIN_NAME}/signout`,
    },
  ];
  const appBody = document.getElementById('app');
  const iframeContainer = document.createElement('div');
  iframeContainer.id = 'signout-iframes';
  appBody.appendChild(iframeContainer);

  const templates = services.map(service =>
    `<iframe v-on:load="hideHeader" width="0" height="0" src="${service.signOutUrl}"></iframe>`,
  );
  templates.forEach((template) => {
    const div = document.createElement('div');
    div.innerHTML = template;
    iframeContainer.appendChild(div);
  });

  setTimeout(() => {
    const iframes = document.getElementById('signout-iframes');
    iframes.parentNode.removeChild(iframes);
  }, 10000);
}


export {
  withdraw,
  checkSession,
  parseUserLogin,
  completeSignUpFlow,
  signUp,
  signIn,
  signOut,
  updateUserMetadata,
  updatePassword,
  saveRegisterRedirect,
  signOutAllServices,
  getApiTokenSilently,
  cacheMyUserId,
  clearMyUserId,
  getMyUserId,
};
